import { type ReactNode, useRef, useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogTitle, DialogTrigger } from '~/components/ui/dialog';

interface YouTubeDialogProps {
  embedUrl: string;
  children: ReactNode;
  /** 'youtube' for YouTube embeds (default), 'video' for direct video URLs (e.g., R2) */
  videoType?: 'youtube' | 'video';
}

function YouTubeDialog({ embedUrl, children, videoType = 'youtube' }: YouTubeDialogProps) {
  const enhancedEmbedUrl = new URL('?autoplay=1', embedUrl).href;
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (isOpen && videoType === 'video' && videoRef.current) {
      videoRef.current.play();
    }
  }, [isOpen, videoType]);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-w-5xl border-0 bg-black p-0">
        <DialogTitle className="hidden">Video Dialog</DialogTitle>
        <DialogDescription className="hidden">Video Dialog</DialogDescription>
        {videoType === 'youtube' ? (
          <iframe
            className="aspect-video h-full w-full border-0"
            width="560"
            height="315"
            src={enhancedEmbedUrl}
            title="YouTube video player"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        ) : (
          <video
            ref={videoRef}
            className="aspect-video h-full w-full border-0"
            src={embedUrl}
            controls
            autoPlay
            playsInline
          >
            <track kind="captions" />
          </video>
        )}
      </DialogContent>
    </Dialog>
  );
}

export { YouTubeDialog, type YouTubeDialogProps };
